/**
 * Home page: hero brand SVG scroll-to-header animation.
 * Only runs on index when hero has brand SVG and header is hidden until scroll.
 */
(function () {
  if (document.documentElement.classList.contains('shopify-design-mode')) return;

  const SCROLL_THRESHOLD_DESKTOP = 80;
  const SCROLL_THRESHOLD_UP_DESKTOP = 50;
  /** Mobile: trigger header reveal after less scroll so the transition feels earlier. */
  const SCROLL_THRESHOLD_MOBILE = 28;
  const SCROLL_THRESHOLD_UP_MOBILE = 18;

  function scrollThreshold() {
    return window.matchMedia('(max-width: 749px)').matches
      ? SCROLL_THRESHOLD_MOBILE
      : SCROLL_THRESHOLD_DESKTOP;
  }

  function scrollThresholdUp() {
    return window.matchMedia('(max-width: 749px)').matches
      ? SCROLL_THRESHOLD_UP_MOBILE
      : SCROLL_THRESHOLD_UP_DESKTOP;
  }
  const ANIMATION_DURATION = 800;
  const ANIMATION_EASING = 'cubic-bezier(0.22, 0.61, 0.36, 1)';

  const heroWrapper = document.getElementById('hero-brand-svg-wrapper');
  const headerComponent = document.getElementById('header-component');
  const headerGroup = document.getElementById('header-group');

  if (!headerComponent || !headerGroup) return;
  if (!headerComponent.hasAttribute('data-home-header-hidden')) return;

  let state = 'initial'; // 'initial' | 'completed'

  function recalcHeaderGroupHeight() {
    if (!headerGroup) return;
    const header = document.querySelector('header-component');
    if (!header) return;

    // When the special home-header-hidden state is active, force header height
    // and header-group height to 0 so the hero doesn't reserve space for it.
    if (header.hasAttribute('data-home-header-hidden')) {
      document.body.style.setProperty('--header-group-height', '0px');
      document.body.style.setProperty('--header-height', '0px');
      return;
    }

    let height = 0;
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const el = children[i];
      if (el instanceof HTMLElement) height += el.offsetHeight;
    }
    if (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling) {
      height += header.offsetHeight;
    }
    document.body.style.setProperty('--header-group-height', height + 'px');
    document.body.style.setProperty('--header-height', header.offsetHeight + 'px');
  }

  function runAnimation() {
    if (state !== 'initial') return;
    state = 'animating';

    if (!heroWrapper) {
      headerComponent.removeAttribute('data-home-header-hidden');
      const headerSection = headerComponent.closest('.header-section');
      if (headerSection) headerSection.classList.add('header-section--revealing');
      requestAnimationFrame(function () {
        recalcHeaderGroupHeight();
        if (headerSection) {
          headerSection.classList.add('header-section--visible');
          requestAnimationFrame(function () {
            headerSection.classList.remove('header-section--revealing', 'header-section--visible');
          });
        }
        state = 'completed';
      });
      return;
    }

    const svgEl = heroWrapper.querySelector('.brand-logo-svg, svg');
    if (!svgEl) {
      state = 'initial';
      return;
    }

    const startRect = heroWrapper.getBoundingClientRect();

    const overlay = document.createElement('div');
    overlay.setAttribute('aria-hidden', 'true');

    overlay.style.cssText = [
      'position: fixed',
      'z-index: 9999',
      'left: 0',
      'top: 0',
      'width: ' + startRect.width + 'px',
      'height: ' + startRect.height + 'px',
      'transform-origin: 0 0',
      'transform: translate(' + startRect.left + 'px, ' + startRect.top + 'px) scale(1)',
      'display: flex',
      'align-items: center',
      'justify-content: center',
      'pointer-events: none',
      'transition: transform ' + ANIMATION_DURATION + 'ms ' + ANIMATION_EASING
    ].join('; ');

    const clone = svgEl.cloneNode(true);
    clone.style.width = '100%';
    clone.style.height = '100%';
    clone.style.objectFit = 'contain';
    var path = clone.querySelector('path');
    if (path) {
      path.style.transition = 'fill ' + ANIMATION_DURATION + 'ms ' + ANIMATION_EASING;
    }
    overlay.appendChild(clone);
    document.body.appendChild(overlay);

    heroWrapper.style.opacity = '0';
    heroWrapper.style.pointerEvents = 'none';

    headerComponent.removeAttribute('data-home-header-hidden');
    const headerSection = headerComponent.closest('.header-section');
    if (headerSection) headerSection.classList.add('header-section--revealing');

    requestAnimationFrame(function () {
      recalcHeaderGroupHeight();

      const logoPlaceholder = document.getElementById('header-brand-svg-placeholder');
      const targetEl = logoPlaceholder && logoPlaceholder.closest('.header-logo')
        ? logoPlaceholder.closest('.header-logo')
        : headerComponent.querySelector('.header__column--center .header-logo');
      const endRectSource = logoPlaceholder || targetEl;
      var endRect = endRectSource ? endRectSource.getBoundingClientRect() : { left: 0, top: 0, width: 100, height: 50 };
      var scaleX = endRect.width / startRect.width;
      var scaleY = endRect.height / startRect.height;
      // Use a uniform scale to avoid vertical or horizontal stretching.
      var scale = Math.min(scaleX, scaleY);
      var endCenterX = endRect.left + endRect.width / 2;
      var endCenterY = endRect.top + endRect.height / 2;
      var targetWidth = startRect.width * scale;
      var targetHeight = startRect.height * scale;
      var targetLeft = endCenterX - targetWidth / 2;
      var targetTop = endCenterY - targetHeight / 2;

      overlay.style.transform = 'translate(' + targetLeft + 'px, ' + targetTop + 'px) scale(' + scale + ')';
      if (path) path.setAttribute('fill', '#1D1D1B');

      if (headerSection) {
        requestAnimationFrame(function () {
          headerSection.classList.add('header-section--visible');
        });
      }

      function onTransitionEnd(e) {
        if (e.target !== overlay || e.propertyName !== 'transform') return;
        overlay.removeEventListener('transitionend', onTransitionEnd);
        // Reveal the header SVG, then on the next frame remove the overlay so they never "disappear" between states
        if (logoPlaceholder) logoPlaceholder.classList.add('home-brand-svg-revealed');
        requestAnimationFrame(function () {
          overlay.remove();
          if (headerSection) {
            headerSection.classList.remove('header-section--revealing', 'header-section--visible');
          }
          state = 'completed';
        });
      }

      overlay.addEventListener('transitionend', onTransitionEnd);
    });
  }

  function runReverseAnimation() {
    if (state !== 'completed') return;
    state = 'animating';

    const logoPlaceholder = document.getElementById('header-brand-svg-placeholder');
    const headerSection = headerComponent.closest('.header-section');
    var targetEl = logoPlaceholder && logoPlaceholder.closest('.header-logo')
      ? logoPlaceholder.closest('.header-logo')
      : headerComponent.querySelector('.header__column--center .header-logo');

    if (!targetEl || !heroWrapper) {
      headerComponent.setAttribute('data-home-header-hidden', '');
      if (logoPlaceholder) logoPlaceholder.classList.remove('home-brand-svg-revealed');
      heroWrapper.style.opacity = '';
      heroWrapper.style.pointerEvents = '';
      state = 'initial';
      return;
    }

    /* Collapse header immediately so no ghost bar and hero doesn't jump when we set data-home-header-hidden at the end */
    if (headerSection) {
      headerSection.classList.remove('header-section--visible');
      headerSection.classList.add('header-section--reverse-animating');
    }
    recalcHeaderGroupHeight();
    // Compute end/start rects *after* collapsing header for consistent geometry.
    // Use placeholder rect so overlay matches the max-width-constrained logo (no post-animation shrink).
    var endRect = logoPlaceholder ? logoPlaceholder.getBoundingClientRect() : targetEl.getBoundingClientRect();
    var startRect = heroWrapper.getBoundingClientRect();
    var scaleX = endRect.width / startRect.width;
    var scaleY = endRect.height / startRect.height;
    var scale = Math.min(scaleX, scaleY);
    var endCenterX = endRect.left + endRect.width / 2;
    var endCenterY = endRect.top + endRect.height / 2;
    var targetWidth = startRect.width * scale;
    var targetHeight = startRect.height * scale;
    var targetLeft = endCenterX - targetWidth / 2;
    var targetTop = endCenterY - targetHeight / 2;

    if (logoPlaceholder) logoPlaceholder.classList.remove('home-brand-svg-revealed');

    var clone = targetEl.querySelector('.brand-logo-svg, svg');
    if (!clone) clone = targetEl.querySelector('svg');
    if (!clone) {
      headerComponent.setAttribute('data-home-header-hidden', '');
      heroWrapper.style.opacity = '';
      heroWrapper.style.pointerEvents = '';
      state = 'initial';
      return;
    }

    clone = clone.cloneNode(true);
    var path = clone.querySelector('path');
    if (path) {
      path.setAttribute('fill', '#1D1D1B');
      path.style.transition = 'fill ' + ANIMATION_DURATION + 'ms ' + ANIMATION_EASING;
    }

    const overlay = document.createElement('div');
    overlay.setAttribute('aria-hidden', 'true');
    overlay.style.cssText = [
      'position: fixed',
      'z-index: 9999',
      'left: 0',
      'top: 0',
      'width: ' + startRect.width + 'px',
      'height: ' + startRect.height + 'px',
      'transform-origin: 0 0',
      'transform: translate(' + targetLeft + 'px, ' + targetTop + 'px) scale(' + scale + ')',
      'display: flex',
      'align-items: center',
      'justify-content: center',
      'pointer-events: none',
      'transition: transform ' + ANIMATION_DURATION + 'ms ' + ANIMATION_EASING
    ].join('; ');

    clone.style.width = '100%';
    clone.style.height = '100%';
    clone.style.objectFit = 'contain';
    overlay.appendChild(clone);
    document.body.appendChild(overlay);

    requestAnimationFrame(function () {
      overlay.style.transform = 'translate(' + startRect.left + 'px, ' + startRect.top + 'px) scale(1)';
      if (path) path.setAttribute('fill', '#FEFDFB');

      function onTransitionEnd(e) {
        if (e.target !== overlay || e.propertyName !== 'transform') return;
        overlay.removeEventListener('transitionend', onTransitionEnd);
        overlay.remove();
        headerComponent.setAttribute('data-home-header-hidden', '');
        if (headerSection) headerSection.classList.remove('header-section--reverse-animating', 'header-section--revealing', 'header-section--visible');
        heroWrapper.style.opacity = '';
        heroWrapper.style.pointerEvents = '';
        state = 'initial';
      }

      overlay.addEventListener('transitionend', onTransitionEnd);
    });
  }

  function onScroll() {
    if (state === 'animating') return;
    if (window.scrollY >= scrollThreshold() && state === 'initial') {
      runAnimation();
    } else if (window.scrollY < scrollThresholdUp() && state === 'completed') {
      runReverseAnimation();
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  if (window.scrollY >= scrollThreshold() && state === 'initial') runAnimation();
})();
